import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react'
import { useLocation } from 'react-router'

import type { WorkspaceItem } from '../../../shared/models/WorkspaceItem'
import WorkspaceExplorer from '../workspace-media-list/components/workspaceExplorer/WorkspaceExplorer'
import { WorkspaceExplorerMode } from '../workspace-media-list/components/workspaceExplorer/enums/WorkspaceExplorerMode'

type WorkspaceExplorerResult = { workspaceId: string; selectedItem?: WorkspaceItem }

export type WorkspaceExplorerDialogOptions = {
  workspaceId: string
  contentMode?: 'dialog' | 'content'
  onConfirm?: (data: WorkspaceExplorerResult) => void
} & (
  | { mode: WorkspaceExplorerMode.Select }
  | {
      mode: WorkspaceExplorerMode.Move | WorkspaceExplorerMode.Copy
      itemId: string
      sourceParentId: string | null
    }
  | { mode: WorkspaceExplorerMode.Upload; parentId: string | null }
  | { mode: WorkspaceExplorerMode.UploadToImagePlayground }
)

interface WorkspaceExplorerDialogContextValue {
  open: (options: WorkspaceExplorerDialogOptions) => void
}

const WorkspaceExplorerDialogContext = createContext<
  WorkspaceExplorerDialogContextValue | undefined
>(undefined)

export function WorkspaceExplorerDialogProvider({ children }: PropsWithChildren) {
  const { key } = useLocation()
  const [state, setState] = useState<WorkspaceExplorerDialogOptions | null>(null)
  const open = useCallback((options: WorkspaceExplorerDialogOptions) => {
    setState(options)
  }, [])
  const close = useCallback(() => {
    setState(null)
  }, [])

  useEffect(() => {
    close()
  }, [key, close])

  const value = useMemo(() => ({ open }), [open])
  const transferOptions =
    state?.mode === WorkspaceExplorerMode.Move || state?.mode === WorkspaceExplorerMode.Copy
      ? state
      : null

  return (
    <WorkspaceExplorerDialogContext.Provider value={value}>
      {children}
      {state && (
        <WorkspaceExplorer
          key={`${state.workspaceId}:${state.mode}:${transferOptions?.itemId ?? ''}`}
          open
          workspaceId={state.workspaceId}
          contentMode={state.contentMode}
          mode={state.mode}
          currentItemId={transferOptions?.itemId}
          sourceParentId={transferOptions?.sourceParentId}
          parentId={state.mode === WorkspaceExplorerMode.Upload ? state.parentId : undefined}
          onClose={close}
          onConfirm={(data) => {
            close()
            state.onConfirm?.(data)
          }}
        />
      )}
    </WorkspaceExplorerDialogContext.Provider>
  )
}

export function useWorkspaceExplorerDialog() {
  const context = useContext(WorkspaceExplorerDialogContext)
  if (!context) {
    throw new Error(
      'useWorkspaceExplorerDialog must be used within a WorkspaceExplorerDialogProvider',
    )
  }
  return context
}
